"use client";

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { CartProductList } from '@/components/public/pricing/cart/cart-product-list';
import { CartOrderSummary } from '@/components/public/pricing/cart/cart-order-summary';
import { LoginPopover } from '@/components/auth/login-popover';
import { motion } from 'framer-motion';
import {
    useGetProductsQuery,
    useGetCartQuery,
    useUpdateCartItemMutation,
    useRemoveCartItemMutation,
} from '@/app/store/api/cartApi';
import type { ProductType, Product, CartData } from '@/types/cart.types';
import { Loader2 } from 'lucide-react';
import { CurrencySelector } from '@/components/public-layout/currency-selector';

// ─── Stable product ordering ─────────────────────────────────────
export const PRODUCT_ORDER: ProductType[] = [
    'PRO_DECK',
    'FAST_DECK',
    'FUNDRAISING_PITCH_DECK',
    'DOCUMENT',
    'MARKET_RESEARCH',
];

// ─── Static fallback descriptions (API may not always include them) ───
export const PRODUCT_DESCRIPTIONS: Record<ProductType, string> = {
    PRO_DECK: 'Full-depth presentations with real research and proper citations throughout.',
    FAST_DECK: 'Structure your thoughts into clean slides quickly, with light research.',
    FUNDRAISING_PITCH_DECK: 'Investor-ready pitch decks built on proven startup storytelling frameworks.',
    DOCUMENT: 'Generate any formal document, from NDAs and reports to proposals and briefs.',
    MARKET_RESEARCH: 'Deep-dive research reports on any topic, with comprehensive sources and analysis.',
};

// Shape expected by CartProductList / CartOrderSummary child components
export interface CartItem {
    id: ProductType;
    name: string;
    price: number;
    currencySymbol?: string;
    quantity: number;
    description: string;
}

interface CartContainerProps {
    onSeeFeatures?: (id: string) => void;
    cartItems: CartItem[];
    cartData: CartData | undefined;
    cartLoading: boolean;
    productsLoading: boolean;
    onUpdateQuantity: (id: ProductType, delta: number) => void;
    isLoginOpen: boolean;
    setIsLoginOpen: (open: boolean) => void;
}

export const CartContainer = ({ 
    onSeeFeatures, 
    cartItems, 
    cartData, 
    cartLoading, 
    productsLoading, 
    onUpdateQuantity,
    isLoginOpen,
    setIsLoginOpen
}: CartContainerProps) => {
    const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

    const handleCheckoutAction = () => {
        if (!isAuthenticated) {
            setIsLoginOpen(true);
        }
    };

    if (productsLoading) {
        return (
            <section className="bg-white py-16 font-jakarta">
                <div className="max-w-[1200px] px-4 md:px-10 mx-auto flex justify-center items-center py-20">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
            </section>
        );
    }

    return (
        <section id="cart-section" className="bg-white py-16 font-jakarta">
            <div className="max-w-[1200px] px-4 md:px-10 mx-auto">
                <div className="mb-12">
                    <h2 className="text-[32px] md:text-[40px] font-bold text-slate-900 mb-3 tracking-tight">
                        Build decks, documents and research in minutes
                    </h2>
                    <p className="text-[16px] text-slate-400 font-medium max-w-xl">
                        Choose what you need, generate as many as you want. Pay only for what you create.
                    </p>
                </div>


                {/* Cart Layout */}
                <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start relative">
                    <div className="flex-1 w-full relative">
                        {/* Currency Selector positioned above the first card (Pro Decks) */}
                        <div className="flex justify-end mb-4">
                            <CurrencySelector />
                        </div>
                        <CartProductList 
                            items={cartItems} 
                            onUpdateQuantity={onUpdateQuantity} 
                            onSeeFeatures={onSeeFeatures}
                        />
                    </div>
                    <div className="w-full lg:w-[400px] shrink-0 sticky top-24" id="cart-order-summary">
                        <CartOrderSummary
                            cartData={cartData}
                            localItems={cartItems}
                            isAuthenticated={isAuthenticated}
                            onCheckout={handleCheckoutAction}
                            cartLoading={cartLoading}
                        />
                    </div>
                </div>
            </div>

            {/* Login Popover for unauthenticated interactions */}
            <LoginPopover isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
        </section>
    );
};
